import { EllipsisVertical, GripVertical, ImagePlus } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import { uniqueId } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useFileQueries from '@dashboard/hooks/queries/useFileQueries';
import { formatBytes } from '@dashboard/services/formatting';
import logException from '@dashboard/services/logException';
import { getImageSrcAndSrcSet } from '@dashboard/services/getUrlFromCdn';
import { confirm } from '@dashboard/lib/confirm';

import { Button as ShadcnButton, buttonVariants } from '@dashboard/components/ui/button';
import Reorderable from '@dashboard/components/Reorderable';
import clsx from 'clsx';

function getFileId(item) {
  const id = item?.file?._id || item?.file;
  return id ? String(id) : null;
}

function readImageDimensions(file) {
  return new Promise(resolve => {
    try {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({});
      };
      img.src = objectUrl;
    } catch {
      resolve({});
    }
  });
}

/**
 * Controlled image gallery. The displayed list is derived from the server
 * `media` prop (the React Query cache) plus any in-flight uploads the server
 * hasn't reflected yet. Adding attaches a single image, deleting detaches one,
 * and only an explicit reorder writes the whole array — mirroring the atomic
 * file-download editor so concurrent uploads can't clobber each other.
 *
 * `maxItems` caps the gallery — an order bump takes 1, since its card has room
 * for one thumbnail. At the cap the dropzone is gone rather than disabled, and
 * reordering with a single slot means nothing, so the handles go too. The
 * server enforces the same cap (ProductService.attachMedia); this only keeps
 * the UI from offering what it would reject.
 */
const Images = ({ media = [], bucket = 'images', maxItems, onAttach, onDetach, onReorder }) => {
  const { sellerId } = useAuth();
  const { uploadFile, deleteFile } = useFileQueries();

  // In-flight uploads that the server's media array doesn't reflect yet.
  const [uploads, setUploads] = useState([]);

  const serverMedia = media || [];
  const serverFileIds = new Set(serverMedia.map(getFileId).filter(Boolean));

  // Keep uploads visible — in-flight ones, and finished ones the server's media
  // array hasn't caught up to yet — until the page query reflects them, then dedupe.
  const pendingUploads = uploads.filter(upload => {
    const id = getFileId(upload);
    return !(id && serverFileIds.has(id));
  });

  const items = [...serverMedia, ...pendingUploads];
  const isAtCapacity = maxItems != null && items.length >= maxItems;
  // A single-slot gallery has nothing to order.
  const isReorderable = maxItems == null || maxItems > 1;

  const onDrop = useCallback(
    async acceptedFiles => {
      const room = maxItems == null ? acceptedFiles.length : Math.max(0, maxItems - items.length);
      const newUploads = acceptedFiles.slice(0, room).map(file =>
        Object.assign(file, {
          key: uniqueId(),
          url: URL.createObjectURL(file),
          isUploading: true,
          isUploaded: false,
          uploadProgress: 0,
        }),
      );

      setUploads(prev => [...prev, ...newUploads]);

      async function upload(file) {
        try {
          const { width, height } = await readImageDimensions(file);
          file.width = width;
          file.height = height;

          const { data, error } = await uploadFile({
            file,
            bucket,
            sellerId,
            onProgress: progress => {
              setUploads(prev =>
                prev.map(item =>
                  item === file ? Object.assign(file, { uploadProgress: progress }) : item,
                ),
              );
            },
          });

          if (error) {
            throw error;
          }

          await onAttach(data._id);

          // Replace the placeholder with the saved media item. It stays visible
          // (carrying its file id) until the server's media array reflects it.
          setUploads(prev =>
            prev.map(item =>
              item === file
                ? {
                    key: file.key,
                    file: data._id,
                    url: data.location,
                    name: data.name,
                    size: data.size,
                    width: data.width,
                    height: data.height,
                  }
                : item,
            ),
          );
        } catch (error) {
          logException({ message: 'Failed to upload file', error });
          setUploads(prev => prev.filter(item => item !== file));
        }
      }

      await Promise.all(newUploads.map(upload));
    },
    [uploadFile, onAttach, bucket, sellerId, maxItems, items.length],
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: 'image/jpeg, image/png, image/jpg, image/gif, image/webp, image/svg+xml',
    maxSize: 10000000,
    multiple: maxItems !== 1,
  });

  async function onDeleteFile(item) {
    const fileId = getFileId(item);

    if (fileId) {
      // Detach before deleting: a failed detach must leave the file in storage,
      // or the product keeps a media entry whose image is gone.
      await onDetach(fileId);
      await deleteFile(fileId);
      setUploads(prev => prev.filter(upload => getFileId(upload) !== fileId));
      return;
    }

    setUploads(prev => prev.filter(upload => upload !== item));
  }

  function onReorderItems(reordered) {
    // Persist only server-backed items; in-flight uploads append once attached.
    onReorder(reordered.filter(getFileId));
  }

  return (
    <div>
      {items.length > 0 && (
        <div className="w-full border border-border rounded-md mb-4 bg-background overflow-hidden">
          <Reorderable.Wrapper>
            <Reorderable
              id="media"
              items={items}
              onReorder={onReorderItems}
              renderItem={({ item, index, dragHandleProps, isDragging }) => (
                <Reorderable.Item isDragging={isDragging} border={index + 1 < items.length}>
                  <File
                    file={item}
                    onDeleteFile={() => onDeleteFile(item)}
                    dragHandleProps={isReorderable ? dragHandleProps : undefined}
                  />
                </Reorderable.Item>
              )}
            />
          </Reorderable.Wrapper>
        </div>
      )}

      <div
        {...getRootProps()}
        className={clsx(
          'transition-colors p-4 border border-dashed border-input rounded-md cursor-pointer bg-background hover:bg-accent/40 hover:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          isDragActive && 'border-solid border-ring bg-accent/40',
          isAtCapacity && 'hidden',
        )}
      >
        <input name="media" id="media" {...getInputProps()} />

        <div className="flex items-center justify-start gap-3">
          {/* The /upload variants only apply in the AI editor's narrow chat uploader. */}
          <div className="flex items-center justify-center h-9 w-9 rounded-md bg-muted text-muted-foreground shrink-0 @max-[14rem]/upload:hidden">
            <ImagePlus aria-hidden="true" strokeWidth={1.75} className="h-4 w-4" />
          </div>

          <div className="space-y-0.5">
            <div className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">
                {maxItems === 1 ? 'Upload an image' : 'Upload images'}
              </span>{' '}
              or drag and drop
            </div>
            <p className="text-xs text-muted-foreground">
              Recommended dimensions: 1280 × 720 (4:3)
            </p>
          </div>
        </div>
      </div>

      {fileRejections.map(({ file, errors }) => (
        <FileRejection key={file.path} file={file} errors={errors} />
      ))}
    </div>
  );
};

function FileRejection({ file, errors }) {
  if (!file.type.startsWith('image/')) {
    return (
      <div className="my-1 text-destructive" key={file.path}>
        {file.name} is not an image
      </div>
    );
  }

  if (file.size > 10000000) {
    return (
      <div className="my-1 text-destructive">
        {`${file.name} is too big (${formatBytes(file.size)}). Max size: 10MB`}
      </div>
    );
  }

  return errors.map(err => (
    <div key={err.key} className="my-1 text-destructive">
      {err.message}
    </div>
  ));
}

function File({ file, onDeleteFile, dragHandleProps }) {
  const [showProgress, setShowProgress] = useState(file.isUploading);

  useEffect(() => {
    if (file.isUploading) {
      setShowProgress(true);
    } else {
      const timer = setTimeout(() => setShowProgress(false), 500);
      return () => clearTimeout(timer);
    }
  }, [file.isUploading]);

  async function onDelete() {
    const del = await confirm('Delete this image?', {
      confirm: 'Delete',
    });

    if (del) {
      try {
        await onDeleteFile(file);
      } catch (error) {
        logException({ message: 'Unable to delete image', error });
      }
    }
  }

  const fileImage = (() => {
    if (file.url.includes('blob:')) {
      return { src: file.url, srcSet: undefined };
    }

    return getImageSrcAndSrcSet({ url: file.url, width: 64, height: 48 });
  })();

  return (
    <div>
      <div className="px-2 py-3">
        <div className="flex flex-row gap-3 items-center justify-start @max-[14rem]/upload:gap-2">
          {dragHandleProps && (
            <div
              {...dragHandleProps}
              className={clsx(
                buttonVariants({ variant: 'ghost', size: 'icon' }),
                'cursor-grab active:cursor-grabbing @max-[14rem]/upload:hidden',
              )}
            >
              <GripVertical className="size-4" />
            </div>
          )}

          <div className="flex-grow-0 flex-shrink-0 overflow-hidden rounded-md">
            <img
              className={clsx(
                'w-16 h-12 object-cover text-transparent @max-[14rem]/upload:w-12 @max-[14rem]/upload:h-9',
                {
                  'opacity-50 transition-opacity': file.isUploading,
                },
              )}
              src={fileImage.src}
              srcSet={fileImage.srcSet}
              alt={file.name}
            />
          </div>

          <div className="min-w-0 truncate">
            <div className="truncate text-xs text-muted-foreground" title={file.name}>
              {file.name}
            </div>
          </div>

          <div className="flex ml-auto gap-2 items-center">
            <ImageActions onDelete={onDelete} />
          </div>
        </div>
      </div>

      {showProgress && (
        <div className="relative w-full h-1 bg-muted">
          <div
            className="absolute h-full bg-foreground transition-all duration-300"
            style={{
              width: `${
                file.uploadProgress == null || file.uploadProgress > 10 ? file.uploadProgress : 1
              }%`,
            }}
          />
        </div>
      )}
    </div>
  );
}

function ImageActions({ onDelete }) {
  return (
    // Non-modal: Delete opens a confirm dialog as the menu closes, and a modal
    // menu then leaves pointer-events: none on the body, freezing the page.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <ShadcnButton variant="ghost" size="icon" aria-label="More options">
          <EllipsisVertical className="size-4" />
        </ShadcnButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onDelete} variant="destructive">
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default Images;
